import { EnumAssemblyStatus } from "@/app/services/generated/enums/enumAssemblyStatus.generated";
import { EnumAssemblyType } from "@/app/services/generated/enums/enumAssemblyType.generated";
import type { Recipe } from "@/app/services/generated/recipeService";
import { recipeService } from "@/app/services/generated/recipeService";
import Guid from "@/app/types/common/guid";
import { computed, onMounted, ref, shallowRef, watch } from "vue";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { Assembly } from "@/app/services/generated/assemblyService";
import {
  assemblyService,
  AssemblyHeader,
  AssemblyIngredientLine,
  AssemblyOutput,
} from "@/app/services/generated/assemblyService";
import { vesselService } from "@/app/services/generated/vesselv2Service";
import { useRouter } from "@/router/router";
import { makeRef, withLoading } from "@/components/composables/componentTools";
import { useAssemblyTypeVariations } from "@/components/pages/production/assemblies/composables/assemblyTypeVariations";
import type { StockLocation } from "@/app/services/generated/stocklocationService";
import { copyRecipeQcTestsToAssembly } from "@/components/pages/production/qctests/composables/assemblyQcTests";
import { convertRecipeProcessesGroupsToAssembly } from "@/components/pages/production/recipes/composables/useRecipeProcesses";
import { buildCache } from "@/app/caching/buildCache";
import type { Maybe } from "@/components/utils/typeHelpers";
import type { Item } from "@/app/services/generated/itemService";

// Extract out the name due to Vuetify `.header` issue
export interface RecipeWithName {
  recipe: Recipe;
  name: string;
  isHidden: boolean;
}

export async function createNewAssembly(
  assemblyType: EnumAssemblyType,
  stockLocation: StockLocation,
  recipe: Maybe<Recipe>,
  outputStockItem: Maybe<{ id: Guid; unitOfMeasureId: Guid }>,
  expectedOutput?: number | null,
  multiplyIngredientsBy?: number,
  urn?: string
): Promise<Assembly> {
  const assemblyHeader = new AssemblyHeader();
  assemblyHeader.id = Guid.create();
  assemblyHeader.assemblyType = assemblyType;
  assemblyHeader.plannedStartDate = tenantService.today;
  assemblyHeader.currentExpectedCompletionDate = tenantService.today;
  assemblyHeader.stockLocationId = stockLocation.id;
  assemblyHeader.autoAllocateUrnAtStart = urn ? false : true;
  assemblyHeader.urn = urn ?? "";

  if (recipe) {
    assemblyHeader.recipeId = recipe.header.id;
    assemblyHeader.bestBefore = recipe.header.bestBeforeDaysFromStart
      ? tenantService.today.plusDays(recipe.header.bestBeforeDaysFromStart)
      : null;
    assemblyHeader.vesselFillColour = recipe.header.vesselFillColour;
  }

  if (outputStockItem) {
    assemblyHeader.outputStillExpected = new AssemblyOutput();
    assemblyHeader.outputStillExpected.stockItemId = outputStockItem.id;
    assemblyHeader.outputStillExpected.unitOfMeasureId =
      outputStockItem.unitOfMeasureId;

    // If we have a product, and a recipe, get the expected output quantity, otherwise 1
    assemblyHeader.outputStillExpected.quantity =
      expectedOutput ?? recipe?.header.output?.quantity ?? 1;
  }

  // If we have a selected recipe, map all of the ingredients to our Assembly Lines
  const expectedLines =
    recipe?.ingredients.map((recipeIngredient) => {
      const expectedIngredientLine = new AssemblyIngredientLine();

      expectedIngredientLine.id = Guid.create();

      // Apply multiplier if defined and applicable
      if (multiplyIngredientsBy && !recipeIngredient.fixedQuantity) {
        expectedIngredientLine.quantityStillExpected =
          tenantService.roundNumber(
            recipeIngredient.quantity * multiplyIngredientsBy
          );
      } else {
        expectedIngredientLine.quantityStillExpected =
          recipeIngredient.quantity;
      }

      expectedIngredientLine.fixedQuantity = recipeIngredient.fixedQuantity;
      expectedIngredientLine.stockItemId = recipeIngredient.stockItemId;
      expectedIngredientLine.unitOfMeasureId = recipeIngredient.unitOfMeasureId;
      expectedIngredientLine.sequence = recipeIngredient.sequence;

      return expectedIngredientLine;
    }) ?? [];

  const processGroups = recipe
    ? convertRecipeProcessesGroupsToAssembly(recipe.processGroups)
    : [];

  const plannedAssembly = await assemblyService.savePlannedAssembly(
    assemblyHeader,
    expectedLines,
    processGroups
  );

  if (recipe) {
    await copyRecipeQcTestsToAssembly(
      recipe.header.id,
      plannedAssembly.header.id
    );
  }

  return plannedAssembly;
}

// Map to a new structure, as there's a problem with Vuetify and objects using '.header'
export function sortAndMapRecipes(
  recipes: Recipe[],
  selectedAssemblyType: EnumAssemblyType,
  selectedOutputItemId: Maybe<Guid>
) {
  return recipes
    .filter((recipe) => recipe.header.assemblyType.equals(selectedAssemblyType))
    .sort((recipeA, recipeB) => {
      // If we don't have an output stock item, don't sort
      if (!selectedOutputItemId) {
        return 0;
      }

      const recipeAOutputIsSelectedOutput =
        recipeA.header.output?.stockItemId.equals(selectedOutputItemId) ??
        false;

      const recipeBOutputIsSelectedOutput =
        recipeB.header.output?.stockItemId.equals(selectedOutputItemId) ??
        false;

      // Otherwise, bubble the recipes with the same output to the top
      return (
        Number(recipeBOutputIsSelectedOutput) -
        Number(recipeAOutputIsSelectedOutput)
      );
    })
    .map(
      (recipe): RecipeWithName => ({
        recipe: recipe,
        name: recipe.header.name,
        isHidden: recipe.header.isHidden,
      })
    );
}

export function useCreateAssembly(
  defaultAssemblyType: Maybe<EnumAssemblyType>,
  defaultRecipe: Maybe<Recipe>
) {
  const { router } = useRouter();

  const cache = buildCache().withItems().build();

  const selectedAssemblyType = makeRef(
    defaultAssemblyType ?? EnumAssemblyType.materialProduction
  );
  const selectedRecipe = ref<RecipeWithName | undefined>();
  const selectedRecipeOutputStockItem = makeRef<Maybe<Item>>(null);
  const allRecipes = shallowRef<Recipe[]>([]);

  const { assemblyRoutes } = useAssemblyTypeVariations(
    selectedAssemblyType,
    ref(EnumAssemblyStatus.open)
  );

  // Map to a new structure, as there's a problem with Vuetify and objects using '.header'
  const mappedRecipes = computed(() =>
    sortAndMapRecipes(
      allRecipes.value,
      selectedAssemblyType.value,
      selectedRecipeOutputStockItem.value?.details.id
    )
  );

  // Only our appropriate stock items to output, based on use selections
  const stockItemsFilteredByAssemblyType = computed(() =>
    cache.items.all.filter((item) => {
      return selectedAssemblyType.value.isMaterialProduction()
        ? item.details.mainPurpose.isMaterial()
        : item.details.mainPurpose.isProduct();
    })
  );

  // Saves our new assembly, and moves us to it
  const createAssembly = withLoading(
    async (opts: {
      defaultVesselId: Maybe<Guid>;
      vesselStockLocationId: Maybe<Guid>;
      start: boolean;
    }) => {
      const slCache = await buildCache().withStockLocations().buildAndUpdate();

      const assemblyStockLocation =
        slCache.stockLocations.getById(opts.vesselStockLocationId) ??
        slCache.stockLocations.resolveRequiredStockLocationForAssembly();

      const newAssembly = await createNewAssembly(
        selectedAssemblyType.value,
        assemblyStockLocation,
        selectedRecipe.value?.recipe,
        selectedRecipeOutputStockItem.value?.details
      );
      if (opts.start) {
        // Only lock a vessel if it is being started too
        await assemblyService.beginAssembly(newAssembly.header.id);

        if (opts.defaultVesselId) {
          await vesselService.lockVesselsForAssembly(newAssembly.header.id, [
            opts.defaultVesselId,
          ]);
        }
      }

      await router.push(
        assemblyRoutes.value.getEditAssemblyRoute(newAssembly.header.id)
      );
    }
  );

  // If our selected recipe changes, update our output stock item to match the recipe
  watch(
    selectedRecipe,
    () => {
      selectedRecipeOutputStockItem.value = cache.items.getById(
        selectedRecipe.value?.recipe.header.output?.stockItemId
      );
    },
    { immediate: true }
  );

  // If we change our assembly type, make sure we reset the values so that the DD's aren't empty
  watch(selectedAssemblyType, () => {
    selectedRecipeOutputStockItem.value = undefined;
    selectedRecipe.value = undefined;
  });

  // Fetch recipe and stockItem data
  onMounted(
    withLoading(async () => {
      allRecipes.value = await recipeService.getAllRecipes();
      await cache.update();

      selectedRecipe.value = mappedRecipes.value.find((mappedRecipe) =>
        defaultRecipe?.header.id.equals(mappedRecipe.recipe.header.id)
      );
    })
  );

  return {
    selectedAssemblyType,
    selectedRecipe,
    selectedRecipeOutputStockItem,
    stockItemsFilteredByAssemblyType,
    mappedRecipes,
    createAssembly,
  };
}
